import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFetcher, useLocation } from 'react-router';

import { getApproximateLocation } from '@jane/bloom/dataAccess/googlePlaces/approximateLocation.client';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { PinInitialStorePayload } from '@jane/bloom/routes/api.session.store/types';
import { IntentSchema } from '@jane/bloom/routes/api.session.store/types';
import { buildFormData } from '@jane/bloom/utils/formData';
import { getStoreIdFromUrl } from '@jane/bloom/utils/storeSwitcher/getStoreIdFromUrl';

/**
 * Client-side fallback for store resolution when server-side IP-based
 * geolocation was unavailable (e.g., missing client IP headers, MaxMind
 * lookup failure, or unknown hosting platform).
 *
 * Only runs when:
 * - The user has no pinned store cookie and server-side resolution failed
 * - OR the URL contains a store ID that differs from the current pinned store
 *
 * Uses the Google Geolocation API to get approximate coordinates, then
 * submits to the session store endpoint to pin the nearest store.
 */
export function useFallbackStoreResolution() {
  const {
    appConfig: { googlePlacesApiKey },
    isBot,
    shoppingPreferences,
    wasStoreInitiallyResolved,
  } = useRootLoaderData();
  const location = useLocation();
  const fetcher = useFetcher({ key: 'fallbackStoreResolution' });
  const submitRef = useRef(fetcher.submit);
  submitRef.current = fetcher.submit;

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const incomingStoreId = useMemo(
    () =>
      getStoreIdFromUrl({
        path: location.pathname,
        searchParams,
      }),
    [location.pathname, searchParams]
  );

  const isChangingStoresViaUrl =
    Boolean(incomingStoreId) &&
    shoppingPreferences?.storeId !== incomingStoreId;

  const needsFallback =
    !isBot &&
    (isChangingStoresViaUrl ||
      (!wasStoreInitiallyResolved && !shoppingPreferences?.hasPinnedStoreId));

  const resolve = useCallback(async () => {
    const approx = await getApproximateLocation(googlePlacesApiKey);

    const formData: PinInitialStorePayload = {
      intent: IntentSchema.enum.initialPin,
      lat: approx.location.lat,
      long: approx.location.lng,
      storeId: incomingStoreId,
    };

    submitRef.current(buildFormData(formData), {
      action: `/api/session/store`,
      method: 'post',
    });
  }, [googlePlacesApiKey, incomingStoreId]);

  useEffect(() => {
    if (!needsFallback) return;
    if (fetcher.state !== 'idle') return;

    resolve();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsFallback]);
}
